'use client'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import Typography from 'src/components/design-system/Typography'
import { searchMeilisearch } from 'src/_services/client/meilisearch.service'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import { Breadcrumbs } from 'src/components/Breadcrumbs'
import SearchResults from './SearchResults'
import {
  getSearchSortOptions,
  getSearchSortParams,
  normalizeSearchSort,
  setSearchSortParam,
} from 'src/_helpers/searchSort'
import { getListQueryParamName } from 'src/_helpers/listQueryParams'

const PER_PAGE = 20

const normalizePage = (value) => {
  const parsedValue = Number.parseInt(String(value || ''), 10)

  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 1
}

const normalizeQuery = (value) => String(value || '').trim()

export default function SearchSimpleResultsClient({
  initialQuery = '',
  initialPage = 1,
  initialResults = null,
  locale,
  routeContext = 'main',
  journalUuid = null,
  publisherId = null,
  initialSortValue = 'relevance',
}) {
  const router = useRouter()
  const translateEntry = useTranslateEntry()
  const title = translateEntry('T_SEARCH_RESULTS_TITLE')
  const showLocalHeader = routeContext === 'journal-domain' || routeContext === 'publisher-domain'
  const [query, setQuery] = useState(normalizeQuery(initialQuery))
  const [page, setPage] = useState(normalizePage(initialPage))
  const [sortValue, setSortValue] = useState(normalizeSearchSort(initialSortValue))
  const [results, setResults] = useState(initialResults)
  const [isLoading, setIsLoading] = useState(false)
  const lastLoadedRef = useRef({
    journalUuid,
    publisherId,
    locale,
    page: normalizePage(initialPage),
    query: normalizeQuery(initialQuery),
    sortValue: normalizeSearchSort(initialSortValue),
  })

  useEffect(() => {
    const nextQuery = normalizeQuery(initialQuery)
    const nextPage = normalizePage(initialPage)

    setQuery(nextQuery)
    setPage(nextPage)
    setSortValue(normalizeSearchSort(initialSortValue))
    setResults(initialResults)
    lastLoadedRef.current = {
      journalUuid,
      publisherId,
      locale,
      page: nextPage,
      query: nextQuery,
      sortValue: normalizeSearchSort(initialSortValue),
    }
  }, [initialPage, initialQuery, initialResults, initialSortValue, journalUuid, locale, publisherId])

  useEffect(() => {
    if (!query) {
      setResults(null)
      return
    }

    if (
      lastLoadedRef.current.locale === locale &&
      lastLoadedRef.current.journalUuid === journalUuid &&
      lastLoadedRef.current.publisherId === publisherId &&
      lastLoadedRef.current.page === page &&
      lastLoadedRef.current.query === query &&
      lastLoadedRef.current.sortValue === sortValue
    ) {
      return
    }

    let isCancelled = false

    const loadResults = async () => {
      setIsLoading(true)

      try {
        const params = {
          q: query,
          page,
          perPage: PER_PAGE,
          lang: locale,
          ...getSearchSortParams(sortValue),
          ...(journalUuid
            ? { 'journalUuids[]': journalUuid, 'types[]': ['journal_article', 'journal_issue', 'journal_page'] }
            : {}),
          ...(publisherId
            ? {
                'publisherIds[]': publisherId,
                'types[]': [
                  'journal',
                  'journal_issue',
                  'journal_article',
                  'journal_page',
                  'monographs',
                  'monograph_chapter',
                ],
              }
            : {}),
        }
        const nextResults = await searchMeilisearch(params)

        if (isCancelled) {
          return
        }

        setResults(nextResults)
        lastLoadedRef.current = {
          journalUuid,
          publisherId,
          locale,
          page,
          query,
          sortValue,
        }
      } catch (error) {
        if (isCancelled) {
          return
        }

        console.error('Search error:', error)
        setResults(null)
      } finally {
        if (!isCancelled) {
          setIsLoading(false)
        }
      }
    }

    loadResults()

    return () => {
      isCancelled = true
    }
  }, [journalUuid, locale, page, publisherId, query, sortValue])

  const handlePageChange = (nextPage) => {
    const safePage = normalizePage(nextPage)
    const params = new URLSearchParams()

    setPage(safePage)

    if (query) {
      params.set('query', query)
    }

    if (journalUuid) {
      params.set('journalUuid', journalUuid)
    }

    setSearchSortParam(params, sortValue, locale)

    if (safePage > 1) {
      params.set(getListQueryParamName('page', locale), String(safePage))
    }

    router.replace(params.toString() ? `?${params.toString()}` : '?', { scroll: false })
  }

  const handleSortValueChange = (nextSortValue) => {
    const safeSortValue = normalizeSearchSort(nextSortValue)
    const params = new URLSearchParams()

    setSortValue(safeSortValue)
    setPage(1)

    if (query) {
      params.set('query', query)
    }

    if (journalUuid) {
      params.set('journalUuid', journalUuid)
    }

    setSearchSortParam(params, safeSortValue, locale)

    router.push(params.toString() ? `?${params.toString()}` : '?', { scroll: false })
  }

  if (!query) {
    return (
      <>
        {showLocalHeader && (
          <>
            <Breadcrumbs breadcrumbs={[]} current={title} classCustom="mb-42" />
            <Typography variant="h1-bold" component="h1" color="text-basic-black" className="mt-42">
              {title}
            </Typography>
          </>
        )}
        <Typography variant="body3-normal" className="mt-25 mb-32 text-center" color="text-basic-gray2">
          {translateEntry('T_SEARCH_EMPTY_MESSAGE')}
        </Typography>
      </>
    )
  }

  return (
    <div>
      {showLocalHeader && (
        <>
          <Breadcrumbs breadcrumbs={[]} current={title} classCustom="mb-42" />
          <Typography variant="h1-bold" component="h1" color="text-basic-black" className="mt-42">
            {title}
          </Typography>
        </>
      )}
      <Typography variant="body3-normal" component="p" color="text-basic-black" className="mt-24">
        {`${translateEntry('T_SEARCH_RESULTS_FOR_PHRASE')} "${query}"`}
      </Typography>
      <div className="pt-32 pb-64">
        {!isLoading && results && (results?.totalHits || 0) === 0 && (
          <Typography variant="body3-normal" className="mt-25 mb-32 text-center" color="text-basic-gray2">
            {translateEntry('T_SEARCH_NO_RESULTS_MESSAGE')}
          </Typography>
        )}
        <SearchResults
          results={results}
          isLoading={isLoading}
          page={page}
          pages={results?.totalPages || 0}
          onPageChange={handlePageChange}
          routeContext={routeContext}
          sortOptions={getSearchSortOptions(translateEntry)}
          sortValue={sortValue}
          onSortValueChange={handleSortValueChange}
        />
      </div>
    </div>
  )
}
